import { createContext, useContext, useRef, useEffect, useCallback,RefObject, ReactNode } from 'react';

// 定義 Context 類型
type RefContextType = {
  refsMap: React.RefObject<Map<string, RefObject<HTMLElement>>>;
  registerRef: (key: string, ref: RefObject<HTMLElement>) => void;
};


// 創建 Context 時提供初始值
export const RefContext = createContext<RefContextType>({
  refsMap: { current: new Map() },
  registerRef: () => {},
});

// 修改 useSharedRefs 為泛型函數
export const useSharedRefs = <T extends HTMLElement>(key: string): RefObject<T> => {
  const { refsMap, registerRef } = useContext(RefContext);
  const ref = useRef<T>(null);

  useEffect(() => {
    // 使用類型斷言來確保型別相容性
    registerRef(key, ref as RefObject<HTMLElement>);
    return () => {
      refsMap.current?.delete(key);
    };
  }, [key, registerRef, refsMap]);

  return ref;
};

// Provider 組件
interface RefProviderProps {
  children: ReactNode;
}

export const RefProvider = ({ children }: RefProviderProps) => {
  const refsMap = useRef<Map<string, React.RefObject<HTMLElement>>>(new Map());

  const registerRef = useCallback((key: string, ref: React.RefObject<HTMLElement>) => {
    refsMap.current.set(key, ref);
  }, []);

  return (
    <RefContext.Provider 
      value={{ 
        refsMap, 
        registerRef 
      }}
    >
      {children}
    </RefContext.Provider>
  );
};

// 可選：添加類型定義
export type { RefContextType };